/* eslint-disable @typescript-eslint/no-explicit-any */
// Production-safe logging utility

type LogLevel = "error" | "warn" | "info" | "debug";

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: number;
  userAgent?: string;
  url?: string;
}

class Logger {
  private isDevelopment: boolean;
  private logBuffer: LogEntry[] = [];
  private maxBufferSize: number = 100;

  constructor() {
    this.isDevelopment = import.meta.env?.DEV || false;
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isDevelopment) return true;

    // In production, only log errors and warnings
    return level === "error" || level === "warn";
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    data?: any
  ): LogEntry {
    return {
      level,
      message,
      data,
      timestamp: Date.now(),
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      url: typeof window !== "undefined" ? window.location.href : undefined,
    };
  }

  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  error(message: string, data?: any): void {
    const entry = this.createLogEntry("error", message, data);
    this.addToBuffer(entry);

    if (this.shouldLog("error")) {
      console.error(`[ERROR] ${message}`, data);
    }
  }

  warn(message: string, data?: any): void {
    const entry = this.createLogEntry("warn", message, data);
    this.addToBuffer(entry);

    if (this.shouldLog("warn")) {
      console.warn(`[WARN] ${message}`, data);
    }
  }

  info(message: string, data?: any): void {
    const entry = this.createLogEntry("info", message, data);
    this.addToBuffer(entry);

    if (this.shouldLog("info")) {
      console.log(`[INFO] ${message}`, data);
    }
  }

  debug(message: string, data?: any): void {
    const entry = this.createLogEntry("debug", message, data);
    this.addToBuffer(entry);

    if (this.shouldLog("debug")) {
      console.log(`[DEBUG] ${message}`, data);
    }
  }

  // Get recent logs for debugging
  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logBuffer.slice(-count);
  }

  // Clear the log buffer
  clearLogs(): void {
    this.logBuffer = [];
  }
}

export const logger = new Logger();
